/**
 * تعريفات العروض ومنطق حالتها — مصدر واحد للحقيقة.
 * تستهلكه الصفحة الرئيسية وصفحة الدفع وصفحة النجاح، حتى لا يختلف الرقم المعروض بين شاشة وأخرى.
 */

/** حدّ افتراضي للشحن المجاني (يُستخدم إذا لم يأتِ من الباك إند) */
export const DEFAULT_FREE_SHIPPING_THRESHOLD = 200;

/** locked: لم يستوفِ العميل الشرط بعد — ready: جاهز للتفعيل — applied: مُطبَّق على الطلب */
export type OfferState = 'locked' | 'ready' | 'applied';
export type OfferTone = 'green' | 'blue' | 'rose' | 'amber';
export type OfferIcon = 'truck' | 'ticket' | 'percent' | 'gift';
/** auto: يُطبَّق تلقائياً عند استيفاء الشرط — checkout: يفعّله العميل بنفسه عند الدفع */
export type OfferApplyMode = 'auto' | 'checkout';

export interface OfferDefinition {
  id: string;
  /** إظهار العرض للعملاء */
  enabled: boolean;
  title: string;
  subtitle: string;
  tone: OfferTone;
  icon: OfferIcon;
  applyMode: OfferApplyMode;
  /** شارة الزاوية مثل "1/2" لعرض محدود عدد الاستخدامات */
  badge?: string;
  /** طبقات خلف البطاقة توحي بعرض قابل للتكرار */
  stacked?: boolean;
}

export interface Offer extends OfferDefinition {
  state: OfferState;
  /** 0..1 — تقدّم العميل نحو استحقاق العرض */
  progress?: number;
  /** المتبقي بالريال لبلوغ الحد الأدنى */
  remaining?: number;
  /** سطر الحالة داخل البطاقة — يتبدّل بين ما قبل التطبيق وما بعده */
  statusText?: string;
}

/** ما نحتاجه من المنتج لحساب الخصم */
export interface DiscountableProduct {
  price: number;
  originalPrice?: number;
  soldOut?: boolean;
}

/** أعلى نسبة خصم فعلية بين المنتجات المعروضة، أو 0 إن لم يوجد خصم */
export function maxDiscountPercent(products: DiscountableProduct[]): number {
  let max = 0;
  for (const product of products) {
    if (product.soldOut === true) continue;
    const price = Number(product.price) || 0;
    const original = Number(product.originalPrice) || 0;
    if (price <= 0 || original <= price) continue;
    const percent = Math.round(((original - price) / original) * 100);
    if (percent > max) max = percent;
  }
  return max;
}

/**
 * بطاقة خصومات المتجر — تظهر فقط إن كان هناك منتج معروض سعره أقل من سعره الأصلي،
 * والنسبة محسوبة من أسعار المنتجات نفسها لا مكتوبة يدوياً.
 */
export function buildStoreDiscountOffer(products: DiscountableProduct[]): Offer | null {
  const percent = maxDiscountPercent(products);
  if (percent <= 0) return null;
  return {
    id: 'store-discounts',
    enabled: true,
    title: 'خصومات المتجر',
    subtitle: `خصم يصل الى ${percent}% على بعض الأطباق`,
    tone: 'rose',
    icon: 'percent',
    applyMode: 'auto',
    state: 'ready',
  };
}

export interface FreeShippingInput {
  /** وصلت استجابة /api/store — قبلها لا نعرض شيئاً */
  storeReady: boolean;
  /** رسوم التوصيل الافتراضية من إعدادات المتجر */
  shipping?: number;
  freeShippingActive?: boolean;
  freeShippingThreshold?: number;
  cartTotal: number;
  /** التوصيل المجاني ممنوح عبر كوبون */
  couponFreeShipping?: boolean;
}

export interface FreeShippingResult {
  /** عرض التوصيل المجاني قائم: مفعّل من إعدادات المتجر وهناك رسوم يمكن إعفاؤها */
  isOfferActive: boolean;
  /** بلغت قيمة السلة الحد الأدنى */
  reachedThreshold: boolean;
  /** مجاني بسبب كوبون أو بلوغ الحد الأدنى */
  isFreeByOffer: boolean;
  /** المتجر لا يفرض رسوم توصيل أصلاً */
  isAlwaysFree: boolean;
  /** رسوم التوصيل النهائية على هذا الطلب */
  effectiveShippingCost: number;
  threshold: number;
  /** المتبقي بالريال (مقرّب لأعلى)، أو 0 إذا لا ينطبق */
  remaining: number;
  progress: number;
  state: OfferState;
}

export function resolveFreeShipping(input: FreeShippingInput): FreeShippingResult {
  const storeReady = input.storeReady === true;
  const shippingCost = Number(input.shipping) || 0;
  const cartTotal = Number(input.cartTotal) || 0;
  const threshold = Number(input.freeShippingThreshold) || DEFAULT_FREE_SHIPPING_THRESHOLD;

  const isOfferActive = storeReady && input.freeShippingActive === true && shippingCost > 0;
  const reachedThreshold = isOfferActive && cartTotal >= threshold;
  const isFreeByOffer = input.couponFreeShipping === true || reachedThreshold;
  const isAlwaysFree = storeReady && shippingCost === 0;

  const remaining = isOfferActive && cartTotal < threshold ? Math.ceil(threshold - cartTotal) : 0;
  const progress =
    isOfferActive && threshold > 0 ? Math.min(1, Math.max(0, cartTotal / threshold)) : 0;

  return {
    isOfferActive,
    reachedThreshold,
    isFreeByOffer,
    isAlwaysFree,
    effectiveShippingCost: isFreeByOffer ? 0 : shippingCost,
    threshold,
    remaining,
    progress,
    state: isFreeByOffer || isAlwaysFree ? 'applied' : 'locked',
  };
}

export interface OfferContext extends FreeShippingInput {
  /** المنتجات المعروضة — لحساب أعلى نسبة خصم */
  products?: DiscountableProduct[];
}

/** نص الحد الأدنى أسفل عنوان بطاقة التوصيل المجاني — القيمة من إعدادات المتجر */
const minOrderLabel = (threshold: number) => `الحد الأدنى لقيمة الطلب ${threshold.toFixed(2)}`;

/** بطاقة التوصيل المجاني — مبنية على إعدادات المتجر الحقيقية، لا على قيم ثابتة */
export function buildFreeShippingOffer(info: FreeShippingResult): Offer | null {
  if (info.isAlwaysFree) {
    return {
      id: 'free-shipping',
      enabled: true,
      title: 'التوصيل مجاني',
      subtitle: 'لا يوجد حد أدنى',
      tone: 'blue',
      icon: 'truck',
      applyMode: 'auto',
      state: 'applied',
      statusText: 'التوصيل مجاني على كل الطلبات',
    };
  }

  if (!info.isOfferActive) return null;

  const applied = info.state === 'applied';
  return {
    id: 'free-shipping',
    enabled: true,
    title: 'التوصيل مجاني',
    subtitle: minOrderLabel(info.threshold),
    tone: 'blue',
    icon: 'truck',
    applyMode: 'auto',
    state: applied ? 'applied' : 'locked',
    progress: info.progress,
    remaining: info.remaining,
    statusText: applied
      ? 'تم تطبيق التوصيل المجاني'
      : info.remaining > 0
        ? `أضف ${info.remaining} ريال ويصير التوصيل مجاني`
        : undefined,
  };
}

/** قائمة العروض المعروضة للعميل — بطاقتان فقط، كلٌّ منهما مشروطة ببيانات حقيقية */
export function buildOffers(ctx: OfferContext): Offer[] {
  if (!ctx.storeReady) return [];

  const offers: Offer[] = [];

  // 1) التوصيل المجاني: فقط عند تفعيله من التطبيق، وبالحد الأدنى القادم منه
  const freeShipping = buildFreeShippingOffer(resolveFreeShipping(ctx));
  if (freeShipping) offers.push(freeShipping);

  // 2) خصومات المتجر: فقط عند وجود منتج معروض بسعر أقل من سعره الأصلي
  const storeDiscount = buildStoreDiscountOffer(ctx.products ?? []);
  if (storeDiscount) offers.push(storeDiscount);

  return offers;
}
